import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  TextField,
  Card,
  CardContent,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: #f5f5f5;
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 24px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;
`;

const SectionContainer = styled(Paper)`
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

// ✅ 추가: 안전한 숫자 포맷 헬퍼 함수
const safeFormatNumber = (value) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const safeToLocaleString = (value) => {
  return safeFormatNumber(value).toLocaleString();
};

function Simulation() {
  const { group_code, project_code } = useParams();

  const [instructionGroups, setInstructionGroups] = useState([]);
  const [instructionInputs, setInstructionInputs] = useState({});
  const [finishCheckboxes, setFinishCheckboxes] = useState({});
  const [stockIncreaseCheckboxes, setStockIncreaseCheckboxes] = useState({});

  const [processedData, setProcessedData] = useState([]);
  const [processedInputs, setProcessedInputs] = useState({});

  const [stockData, setStockData] = useState([]);

  // ✅ 추가: 재고 수정 상태 관리
  const [editingStockId, setEditingStockId] = useState(null);
  const [stockInputs, setStockInputs] = useState({});

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchAllData();
  }, [group_code, project_code]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchInstructions(),
        fetchProcessedData(),
        fetchStockData(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar("데이터 로딩 실패", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetAllData = async () => {
    if (window.confirm("데이터를 초기화하시겠습니까?")) {
      try {
        const res = await axios.get(
          `${TPC_URL}/api/tpc/resetAllData?group_code=${group_code}&project_code=${project_code}`
        );
        if (res.status === 200) {
          showSnackbar("데이터 초기화 완료", "success");
          await fetchAllData();
        } else {
          showSnackbar("데이터 초기화 실패", "error");
        }
      } catch (e) {
        console.error("Error resetting data:", e);
        showSnackbar("데이터 초기화 실패", "error");
      }
    }
  };

  const fetchInstructions = async () => {
    try {
      const response = await axios.get(
        `${TPC_URL}/api/instruction/getAllInstructions`,
        {
          params: { group_code, project_code },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        const unfinished = response.data.filter(
          (item) => item.is_finished === 0
        );

        const grouped = {};
        const initialInputs = {};

        unfinished.forEach((item) => {
          const instId = item.instruction_id;

          if (!grouped[instId]) {
            grouped[instId] = {
              instruction_id: item.instruction_id,
              instruction_code: item.instruction_code,
              register_date: item.register_date,
              instruction_date: item.instruction_date,
              items: [],
            };
          }

          const itemKey = `${item.item_id}_${item.instruction_date}_${item.shift_id}`;
          const existingItem = grouped[instId].items.find(
            (i) =>
              `${i.item_id}_${i.instruction_date}_${i.shift_id}` === itemKey
          );

          if (existingItem) {
            existingItem.result_qty =
              safeFormatNumber(existingItem.result_qty) +
              safeFormatNumber(item.result_qty);
          } else {
            grouped[instId].items.push({ ...item });

            const key = `${item.instruction_id}_${item.item_id}_${item.instruction_date}_${item.shift_id}`;
            const customQty = safeFormatNumber(item.custom_qty);
            const aiQty = safeFormatNumber(item.ai_qty);
            const displayQty = customQty !== 0 ? customQty : aiQty;

            initialInputs[key] = displayQty;
          }
        });

        const groupedArray = Object.values(grouped).sort((a, b) =>
          dayjs(b.register_date).diff(dayjs(a.register_date))
        );

        setInstructionGroups(groupedArray);

        setInstructionInputs((prev) => ({
          ...initialInputs,
          ...prev,
        }));
      }
    } catch (error) {
      console.error("Error fetching instructions:", error);
      throw error;
    }
  };

  const fetchProcessedData = async () => {
    try {
      const response = await axios.get(`${TPC_URL}/api/tpc/getProcessedData`, {
        params: { group_code, project_code },
      });

      if (response.data && Array.isArray(response.data)) {
        const unfinished = response.data.filter((item) => item.status === 0);
        setProcessedData(unfinished);
      }
    } catch (error) {
      console.error("Error fetching processed data:", error);
      throw error;
    }
  };

  const fetchStockData = async () => {
    try {
      const response = await axios.get(`${TPC_URL}/api/stock/list`, {
        params: { group_code, project_code },
      });

      if (response.data && Array.isArray(response.data)) {
        setStockData(response.data);
        
        // ✅ 추가: 초기 재고 입력값 설정
        const initialStockInputs = {};
        response.data.forEach((stock) => {
          initialStockInputs[stock.stock_adjust_id] = safeFormatNumber(stock.actual_stock);
        });
        setStockInputs(initialStockInputs);
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
      throw error;
    }
  };

  const handleInstructionInputChange = (key, value) => {
    setInstructionInputs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFinishCheckboxChange = (key, checked) => {
    setFinishCheckboxes((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleStockIncreaseCheckboxChange = (key, checked) => {
    setStockIncreaseCheckboxes((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSaveResult = async (item) => {
    const key = `${item.instruction_id}_${item.item_id}_${item.instruction_date}_${item.shift_id}`;
    const qty = parseInt(instructionInputs[key]) || 0;
    const isFinished = finishCheckboxes[key] || false;
    const increaseStock = stockIncreaseCheckboxes[key] || false;

    const customQty = safeFormatNumber(item.custom_qty);
    const aiQty = safeFormatNumber(item.ai_qty);
    const displayQty = customQty !== 0 ? customQty : aiQty;

    if (qty <= 0 && !(displayQty === 0 && isFinished)) {
      showSnackbar("수량을 입력해주세요", "warning");
      return;
    }

    try {
      await axios.post(`${TPC_URL}/api/instruction/saveInstructionResult`, {
        group_code,
        project_code,
        instruction_id: item.instruction_id,
        item_id: item.item_id,
        instruction_date: dayjs(item.instruction_date).format("YYYY-MM-DD"),
        result_qty: qty,
        is_finished: isFinished,
        increase_stock: increaseStock,
      });

      let message = "실적이 저장되었습니다";
      if (isFinished && increaseStock) {
        message += " (마감 완료 + 재고 증가)";
      } else if (isFinished) {
        message += " (마감 완료)";
      } else if (increaseStock) {
        message += " (재고 증가)";
      }

      showSnackbar(message, "success");

      setInstructionInputs((prev) => ({
        ...prev,
        [key]: "",
      }));
      setFinishCheckboxes((prev) => ({
        ...prev,
        [key]: false,
      }));
      setStockIncreaseCheckboxes((prev) => ({
        ...prev,
        [key]: false,
      }));

      await fetchAllData();
    } catch (error) {
      console.error("Error saving result:", error);
      showSnackbar(error.response?.data?.error || "실적 저장 실패", "error");
    }
  };

  const handleProcessedInputChange = (orderNumber, value) => {
    setProcessedInputs((prev) => ({
      ...prev,
      [orderNumber]: value,
    }));
  };

  const handleProcessedDeliver = async (item) => {
    const productionQty = safeFormatNumber(item.production_quantity);

    if (
      !window.confirm(
        `출고 처리 시 재고가 ${productionQty}개 차감됩니다. 계속하시겠습니까?`
      )
    ) {
      return;
    }

    try {
      await axios.post(`${TPC_URL}/api/tpc/deliverProcessedData`, {
        group_code,
        project_code,
        order_number: item.order_number,
        production_quantity: productionQty,
      });

      await axios.post(`${TPC_URL}/api/stock/decrease`, {
        group_code,
        project_code,
        item_code: item.add_descr_4,
        qty: productionQty,
      });

      showSnackbar(`출고 완료 (재고 ${productionQty}개 차감)`, "success");
      await fetchAllData();
    } catch (error) {
      console.error("Error:", error);
      showSnackbar(error.response?.data?.error || "처리 실패", "error");
    }
  };

  // ✅ 추가: 재고 수정 관련 함수들
  const handleStockEdit = (stockId) => {
    setEditingStockId(stockId);
  };

  const handleStockInputChange = (stockId, value) => {
    setStockInputs((prev) => ({
      ...prev,
      [stockId]: value,
    }));
  };

  const handleStockSave = async (stock) => {
    const newQty = parseInt(stockInputs[stock.stock_adjust_id]) || 0;
    
    if (newQty < 0) {
      showSnackbar("재고는 0 이상이어야 합니다", "warning");
      return;
    }
  
    try {
      // ✅ API 호출
      const response = await axios.post(`${TPC_URL}/api/stock/setStock`, {
        group_code,
        project_code,
        item_code: stock.item_code,
        qty: newQty,
      });
  
      // ✅ 성공 처리
      if (response.status === 200) {
        showSnackbar(`재고가 ${newQty}개로 변경되었습니다`, "success");
        setEditingStockId(null);
        
        // ✅ 재고 데이터 재조회
        await fetchStockData();
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      showSnackbar(
        error.response?.data?.error || "재고 수정 실패", 
        "error"
      );
    }
  };
  
  const handleStockCancel = (stock) => {
    setStockInputs((prev) => ({
      ...prev,
      [stock.stock_adjust_id]: safeFormatNumber(stock.actual_stock),
    }));
    setEditingStockId(null);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCompleteAll = async (group) => {
    const confirmMessage = `"${
      group.instruction_code
    }" 작업지시서의 모든 항목을 마감하고 재고를 증가시킵니다.\n\n처리할 항목:\n${group.items
      .map((item) => {
        const customQty = safeFormatNumber(item.custom_qty);
        const aiQty = safeFormatNumber(item.ai_qty);
        const resultQty = safeFormatNumber(item.result_qty);
        const displayQty = customQty !== 0 ? customQty : aiQty;
        const remainingQty = displayQty - resultQty;
        return `- ${item.item_code}: ${remainingQty}개`;
      })
      .join("\n")}\n\n계속하시겠습니까?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const item of group.items) {
        const customQty = safeFormatNumber(item.custom_qty);
        const aiQty = safeFormatNumber(item.ai_qty);
        const resultQty = safeFormatNumber(item.result_qty);
        const displayQty = customQty !== 0 ? customQty : aiQty;
        const remainingQty = displayQty - resultQty;

        try {
          await axios.post(`${TPC_URL}/api/instruction/saveInstructionResult`, {
            group_code,
            project_code,
            instruction_id: item.instruction_id,
            item_id: item.item_id,
            instruction_date: dayjs(item.instruction_date).format(
              "YYYY-MM-DD"
            ),
            result_qty: remainingQty,
            is_finished: true,
            increase_stock: true,
          });

          successCount++;
        } catch (error) {
          console.error(`Error processing ${item.item_code}:`, error);
          failCount++;
        }
      }

      let message = `전체 마감 완료\n성공: ${successCount}개`;
      if (failCount > 0) {
        message += `, 실패: ${failCount}개`;
      }

      showSnackbar(message, failCount > 0 ? "warning" : "success");

      group.items.forEach((item) => {
        const key = `${item.instruction_id}_${item.item_id}_${item.instruction_date}_${item.shift_id}`;
        setInstructionInputs((prev) => ({
          ...prev,
          [key]: "",
        }));
        setFinishCheckboxes((prev) => ({
          ...prev,
          [key]: false,
        }));
        setStockIncreaseCheckboxes((prev) => ({
          ...prev,
          [key]: false,
        }));
      });

      await fetchAllData();
    } catch (error) {
      console.error("Error in handleCompleteAll:", error);
      showSnackbar("전체 마감 처리 중 오류 발생", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress size={60} />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "22px !important", color: "#111" }}
          >
            재고 시뮬레이션
          </Typography>
          <Button variant="contained" color="primary" onClick={resetAllData}>
            데이터 초기화
          </Button>
        </ToolbarContainer>

        <ContentContainer>
          {/* 작업지시서 섹션 */}
          <SectionContainer elevation={3}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              작업지시서 실적 입력
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {instructionGroups.length === 0 ? (
              <Typography color="text.secondary" align="center">
                마감되지 않은 작업지시서가 없습니다
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {instructionGroups.map((group) => (
                  <Grid item xs={12} key={group.instruction_id}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: "2px solid #1976d2",
                        bgcolor: "#f5f9ff",
                      }}
                    >
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                          pb={1}
                          borderBottom="1px solid #e0e0e0"
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {group.instruction_code}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              등록:{" "}
                              {dayjs(group.register_date).format(
                                "MM-DD HH:mm"
                              )}
                            </Typography>
                          </Box>
                          <Box display="flex" gap={1} alignItems="center">
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => handleCompleteAll(group)}
                              sx={{
                                fontWeight: "bold",
                                boxShadow: 2,
                              }}
                            >
                              전체마감
                            </Button>
                          </Box>
                        </Box>

                        <Grid container spacing={1.5}>
                          {group.items.map((item) => {
                            const key = `${item.instruction_id}_${item.item_id}_${item.instruction_date}_${item.shift_id}`;
                            const customQty = safeFormatNumber(item.custom_qty);
                            const aiQty = safeFormatNumber(item.ai_qty);
                            const resultQty = safeFormatNumber(item.result_qty);
                            const displayQty =
                              customQty !== 0 ? customQty : aiQty;

                            return (
                              <Grid item xs={12} key={key}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    bgcolor: "white",
                                    border: "1px solid #e0e0e0",
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                  >
                                    <Box>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                      >
                                        {item.item_code}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {item.material_grouping}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      color="primary"
                                    >
                                      {dayjs(item.instruction_date).format(
                                        "MM-DD"
                                      )}{" "}
                                      | {item.shift_name}
                                    </Typography>
                                  </Box>

                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                  >
                                    <Box flex={1}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        지시: {safeToLocaleString(displayQty)}
                                      </Typography>
                                    </Box>
                                    <Box flex={1}>
                                      <Typography
                                        variant="caption"
                                        color="primary"
                                      >
                                        완료: {safeToLocaleString(resultQty)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box display="flex" gap={1} mb={1}>
                                    <TextField
                                      placeholder="실적"
                                      type="number"
                                      size="small"
                                      fullWidth
                                      value={instructionInputs[key] || ""}
                                      onChange={(e) =>
                                        handleInstructionInputChange(
                                          key,
                                          e.target.value
                                        )
                                      }
                                      InputProps={{
                                        inputProps: { min: 0 },
                                      }}
                                    />
                                  </Box>

                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Box display="flex" gap={1}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={
                                              finishCheckboxes[key] || false
                                            }
                                            onChange={(e) =>
                                              handleFinishCheckboxChange(
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        }
                                        label={
                                          <Typography variant="caption">
                                            마감
                                          </Typography>
                                        }
                                        sx={{ m: 0 }}
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={
                                              stockIncreaseCheckboxes[key] ||
                                              false
                                            }
                                            onChange={(e) =>
                                              handleStockIncreaseCheckboxChange(
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        }
                                        label={
                                          <Typography variant="caption">
                                            재고증가
                                          </Typography>
                                        }
                                        sx={{ m: 0 }}
                                      />
                                    </Box>

                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<SaveIcon />}
                                      onClick={() => handleSaveResult(item)}
                                    >
                                      입력
                                    </Button>
                                  </Box>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionContainer>

          {/* Processed 섹션 */}
          <SectionContainer elevation={3}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              주문 출고 처리
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {processedData.length === 0 ? (
              <Typography color="text.secondary" align="center">
                미완료 주문이 없습니다
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {processedData.map((item) => (
                  <Grid item xs={12} key={item.order_number}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box mb={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {item.order_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.material_grouping}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                          >
                            납기: {item.local_due_date || "미정"}
                          </Typography>
                        </Box>

                        <Box mb={1}>
                          <Typography variant="caption" color="text.secondary">
                            생산 지시
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {safeToLocaleString(item.production_quantity)}
                          </Typography>
                        </Box>

                        <Box display="flex" gap={1}>
                          <Button
                            variant="contained"
                            size="small"
                            color="warning"
                            fullWidth
                            onClick={() => handleProcessedDeliver(item)}
                          >
                            출고
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionContainer>

          {/* ✅ 수정: 재고 현황 섹션 (편집 기능 추가) */}
          <SectionContainer elevation={3}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              현재 재고 현황
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {stockData.length === 0 ? (
              <Typography color="text.secondary" align="center">
                재고 데이터가 없습니다
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {stockData.map((stock) => {
                  const isEditing = editingStockId === stock.stock_adjust_id;

                  return (
                    <Paper
                      key={stock.stock_adjust_id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: isEditing ? "2px solid #1976d2" : "1px solid #e0e0e0",
                        borderRadius: 1,
                        transition: "all 0.2s",
                        bgcolor: isEditing ? "#f5f9ff" : "white",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {stock.material_grouping}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            ({stock.item_code})
                          </Typography>
                        </Box>

                        {isEditing ? (
                          // ✅ 편집 모드
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <TextField
                              type="number"
                              size="small"
                              value={stockInputs[stock.stock_adjust_id] || ""}
                              onChange={(e) =>
                                handleStockInputChange(
                                  stock.stock_adjust_id,
                                  e.target.value
                                )
                              }
                              sx={{
                                width: 100,
                                "& input": {
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  fontSize: "1.1rem",
                                },
                              }}
                              InputProps={{
                                inputProps: { min: 0 },
                              }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleStockSave(stock)}
                              sx={{
                                bgcolor: "#e3f2fd",
                                "&:hover": { bgcolor: "#1976d2", color: "white" },
                              }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleStockCancel(stock)}
                              sx={{
                                bgcolor: "#ffebee",
                                "&:hover": { bgcolor: "#d32f2f", color: "white" },
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          // ✅ 일반 모드
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: "#e3f2fd",
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                minWidth: 60,
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="primary"
                                sx={{ fontSize: "1.1rem" }}
                              >
                                {safeToLocaleString(stock.actual_stock)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleStockEdit(stock.stock_adjust_id)}
                              sx={{
                                bgcolor: "#f5f5f5",
                                "&:hover": { bgcolor: "#e0e0e0" },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </SectionContainer>
        </ContentContainer>
      </FrameContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default Simulation;
